import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { AlertTriangle, RefreshCw, Trash2, Smartphone, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ConfirmDialog';

const isMobileUA = (ua = '') => /Mobi|Android|iPhone|iPad/i.test(ua);

export default function AdminClientErrors() {
  const [data, setData] = useState({ total: 0, errors: [] });
  const [loading, setLoading] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/client-errors');
      setData(res.data);
    } catch (error) {
      logger.error('Error fetching client errors:', error);
      toast.error('Failed to load client errors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchErrors(); }, []);

  const handleClear = async () => {
    try {
      await api.delete('/admin/client-errors');
      toast.success('Error log cleared');
      setConfirmClear(false);
      fetchErrors();
    } catch (error) {
      logger.error('Error clearing client errors:', error);
      toast.error('Failed to clear errors');
    }
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="admin-client-errors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
            <AlertTriangle className="w-6 h-6" style={{ color: '#9E6752' }} />
            Client Errors
          </h1>
          <p className="text-sm mt-1" style={{ color: '#73766A' }}>
            Crashes reported from users&apos; browsers, {data.total} total logged
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchErrors} data-testid="client-errors-refresh-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmClear(true)}
            disabled={data.total === 0}
            data-testid="client-errors-clear-button"
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Clear All
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: '#73766A' }}>Loading…</p>
      ) : data.errors.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center" data-testid="client-errors-empty-state">
          <p className="font-semibold" style={{ color: '#20212B' }}>No client errors logged 🎉</p>
          <p className="text-sm mt-1" style={{ color: '#73766A' }}>
            When any user&apos;s browser crashes, the details will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.errors.map((err) => (
            <div
              key={err.id}
              className="rounded-xl border bg-white p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setExpanded(expanded === err.id ? null : err.id)}
              data-testid={`client-error-row-${err.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-sm font-semibold truncate" style={{ color: '#B91C1C' }}>
                    {err.message}
                  </p>
                  <p className="text-xs mt-1 truncate" style={{ color: '#73766A' }}>
                    {err.url}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-xs" style={{ color: '#73766A' }}>
                  {isMobileUA(err.user_agent)
                    ? <Smartphone className="w-4 h-4" style={{ color: '#9E6752' }} />
                    : <Monitor className="w-4 h-4" />}
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 font-medium">{err.source}</span>
                  <span>{err.viewport}</span>
                  <span>{new Date(err.created_at).toLocaleString()}</span>
                </div>
              </div>
              {expanded === err.id && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs break-all" style={{ color: '#73766A' }}>{err.user_agent}</p>
                  {err.stack && (
                    <pre className="text-xs bg-gray-50 rounded-lg p-3 overflow-x-auto max-h-64" style={{ color: '#20212B' }}>
                      {err.stack}
                    </pre>
                  )}
                  {err.component_stack && (
                    <pre className="text-xs bg-orange-50 rounded-lg p-3 overflow-x-auto max-h-48" style={{ color: '#20212B' }}>
                      {err.component_stack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title="Clear all client errors?"
        description="This permanently deletes every logged error report."
      confirmText="Clear All"
        onConfirm={handleClear}
      />
    </div>
  );
}
